import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCol,
  CRow,
  CFormLabel,
  CFormInput,
  CButton,
  CContainer,
  CAvatar,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CTooltip,
  CFormSwitch,
} from "@coreui/react";
import dummyAvatar from "../../assets/avatar.png";
import { useDispatch, useSelector } from "react-redux";
import {
  clearBuyerMessages,
  editImage,
  fetchUserProfile,
  updateUserProfile,
  fetchTradePreferenceOptions,
  fetchMyTradePreference,
  saveTradePreference,
} from "../../features/user/userSlice";
import AppLoader from "../../components/seller/AppLoader";
import AppToast from "../../components/seller/AppToast";
import * as Yup from "yup";
import { capitalizeFirst } from "../../utils/stringUtils";
import {
  FaEraser,
  FaImage,
  FaMagnifyingGlassChart,
  FaSpinner,
  FaWandMagicSparkles,
  FaWandSparkles,
} from "react-icons/fa6";
import { FaMagic } from "react-icons/fa";
import { CircularProgress } from "@mui/material";
import { COUNTRY_CODES } from "../../utils/countryCodes";

const splitPhone = (rawPhone = "") => {
  if (rawPhone.startsWith("+")) {
    const sorted = [...COUNTRY_CODES].sort(
      (a, b) => b.code.length - a.code.length,
    );
    const match = sorted.find((c) => rawPhone.startsWith(c.code));
    if (match) {
      return {
        countryCode: match.code,
        localNumber: rawPhone.slice(match.code.length),
      };
    }
  }
  return { countryCode: "+61", localNumber: rawPhone };
};

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9]+$/, "Must be only digits")
    .min(7, "Too short")
    .required("Phone is required"),
  company_name: Yup.string().required("Company name is required"),
  trade_preferences: Yup.array().min(1, "Select at least one trade preference"),
});

const SellerEditProfile = () => {
  const dispatch = useDispatch();
  const {
    profile,
    profileLoading,
    tradeOptions = [],
    myPreference = [],
  } = useSelector((state) => state.user);
  const { editImageLoading, editImageError, editImageSuccess } = useSelector(
    (state) => state.user,
  );
  const [saving, setSaving] = useState(false);

  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+61",
    company_name: "",
    // communication_email: "",
    // communication_phone: "",
    // is_email_visible: 0,
    // is_phone_visible: 0,
    trade_preferences: [],
    role: "Seller",
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [tempAiPreview, setTempAiPreview] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("info");

  // console.log('My Preference:', myPreference);

  // Fetch all required data
  useEffect(() => {
    dispatch(fetchUserProfile()).unwrap().catch(console.error);
    dispatch(fetchTradePreferenceOptions()).unwrap().catch(console.error);
    dispatch(fetchMyTradePreference()).unwrap().catch(console.error);

    return () => dispatch(clearBuyerMessages());
  }, [dispatch]);

  // Set form values once profile AND myPreference are loaded
  useEffect(() => {
    if (!profile || tradeOptions.length === 0 || saving) return;

    const prefs =
      Array.isArray(myPreference) && myPreference.length > 0
        ? myPreference
        : Array.isArray(profile.trade_preferences)
          ? profile.trade_preferences
          : [];

    // ── Split phone from API into dial code + local number ──────────────────
    const { countryCode, localNumber } = splitPhone(profile.phone || "");

    setFormValues({
      name: profile.name || "",
      email: profile.email || "",
      phone: localNumber,
      countryCode: countryCode || "+61",
      // communication_email: profile.communication_email || "",
      // communication_phone: profile.communication_phone || "",
      // is_email_visible: profile.is_email_visible ?? 0,
      // is_phone_visible: profile.is_phone_visible ?? 0,
      company_name: profile.company_name || "",
      trade_preferences: prefs.map((p) =>
        typeof p === "object" ? Number(p.id) : Number(p),
      ),
      role: profile.role || "Seller",
    });

    if (profile.profile_image_url) {
      // Add cache-busting parameter to prevent browser caching
      const timestamp = new Date().getTime();
      const imageUrl = profile.profile_image_url.includes("?")
        ? `${profile.profile_image_url}&t=${timestamp}`
        : `${profile.profile_image_url}?t=${timestamp}`;
      setPreviewImage(imageUrl);
    }
  }, [profile, myPreference, tradeOptions, saving]);

  // Cleanup on unmount to prevent state leakage
  useEffect(() => {
    return () => {
      setPreviewImage(null);
      setSelectedFile(null);
      setTempAiPreview(null);
    };
  }, []);

  // Handle input changes
  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Validate form
  const validateForm = async () => {
    try {
      await validationSchema.validate(formValues, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const fieldErrors = {};
      err.inner.forEach((e) => (fieldErrors[e.path] = e.message));
      setErrors(fieldErrors);
      return false;
    }
  };

  // Handle AI edits
  const handleEdit = async (action) => {
    try {
      console.log("Starting AI edit with action:", action);
      let fileToSend = selectedFile || previewImage;
      if (!fileToSend) {
        console.warn("No image found to send to AI");
        return;
      }

      if (typeof fileToSend === "string") {
        console.log(
          "Fetching existing image from URL for processing:",
          fileToSend,
        );
        const response = await fetch(fileToSend);
        const blob = await response.blob();
        fileToSend = new File([blob], "profile.png", { type: blob.type });
      }

      const formData = new FormData();
      formData.append("image", fileToSend);
      formData.append("action", action);

      const result = await dispatch(editImage(formData)).unwrap();
      console.log("AI edit successful. Response:", result);

      const editedUrl =
        result?.data?.edited_image || result?.data?.image_base64;
      if (editedUrl) {
        let finalUrl = editedUrl;
        // If it looks like raw base64, add the prefix
        if (
          typeof editedUrl === "string" &&
          !editedUrl.startsWith("http") &&
          !editedUrl.startsWith("data:") &&
          !editedUrl.startsWith("blob:")
        ) {
          finalUrl = `data:image/png;base64,${editedUrl}`;
        }

        console.log(
          "Setting temp AI preview URL:",
          finalUrl.substring(0, 50) + "...",
        );
        setTempAiPreview(finalUrl);
      } else {
        console.warn(
          "API response did not contain edited_image or image_base64",
        );
      }
    } catch (err) {
      console.error("AI edit failed in component:", err);
    }
  };

  // Submit form
  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    try {
      setSaving(true); // show loader on form only

      const payload = {
        name: formValues.name.trim(),
        email: formValues.email.trim(),
        phone: `${formValues.countryCode}${formValues.phone.trim()}`,
        // communication_email: formValues.communication_email.trim(),
        // communication_phone: formValues.communication_phone.trim(),
        // is_email_visible: formValues.is_email_visible,
        // is_phone_visible: formValues.is_phone_visible,
        company_name: formValues.company_name.trim(),
        role: formValues.role,
        trade_preferences: formValues.trade_preferences.join(","),
        profile_image: selectedFile || null,
      };

      const res = await dispatch(updateUserProfile(payload)).unwrap();

      if (formValues.trade_preferences?.length > 0) {
        await dispatch(
          saveTradePreference({ preferences: formValues.trade_preferences }),
        ).unwrap();
      }

      setToastType("success");
      setToastMessage(res?.message || "Profile updated successfully");
      setToastVisible(true);

      // navigate after success
      setTimeout(() => {
        window.history.back();
      }, 1500);
    } catch (err) {
      setToastType("error");
      setToastMessage(err?.message || "Failed to update profile");
      setToastVisible(true);
      setSaving(false);
    } finally {
      // If success, we don't setSaving(false) because we are navigating away
      // and we want the loader to persist until unmount/navigation.
      // But if we caught an error, we already setSaving(false).
      // However, to be safe and consistent with typical patterns (and if nav is slow):
      // setSaving(false);
      // The user wants AppLoader TO SHOW while saving.
      // If we navigate back immediately, it's fine.
      // If we error, we MUST turn off saving.
    }
  };

  const applyAiPreview = async () => {
    if (!tempAiPreview) return;

    try {
      console.log("Applying AI preview to form. URL:", tempAiPreview);
      const res = await fetch(tempAiPreview);
      const blob = await res.blob();
      const file = new File([blob], "ai_profile.png", { type: blob.type });
      console.log("Created file from AI preview:", file);

      setSelectedFile(file); // Update File for submission
      setPreviewImage(tempAiPreview); // Update URL for main UI preview
      setTempAiPreview(null); // Clear modal preview
      setShowAiModal(false);
    } catch (err) {
      console.error("Failed to apply AI preview:", err);
    }
  };

  if ((!profile && profileLoading) || saving) {
    return (
      <>
        <AppLoader />
        <AppToast
          visible={toastVisible}
          setVisible={setToastVisible}
          type={toastType}
          message={toastMessage}
        />
      </>
    );
  }

  return (
    <>
      <CContainer fluid className="px-3 mt-4">
        <CCard className="shadow-sm border-0 rounded-4">
          <CCardBody>
            <h4 className="mb-4 text-primary">Edit Profile</h4>

            <CRow className="mb-4 align-items-center">
              <CCol md={6} className="text-center" style={{ minHeight: 180 }}>
                <div
                  style={{
                    width: "150px",
                    height: "150px",
                    margin: "0 auto",
                    overflow: "hidden",
                    borderRadius: "50%",
                    border: "1px solid #ddd",
                  }}
                >
                  <img
                    src={
                      tempAiPreview ||
                      (selectedFile && URL.createObjectURL(selectedFile)) ||
                      previewImage ||
                      dummyAvatar
                    }
                    alt="Profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
                {/* <div className="mt-2 d-flex gap-2 justify-content-center">
                  <CTooltip
                    content="Use AI to enhance or remove background from your profile picture"
                    placement="bottom"
                  >
                    <CButton
                      size="sm"
                      color="secondary"
                      onClick={() => setShowAiModal(true)}
                    >
                      <FaWandMagicSparkles className="me-2" />
                      AI Photo Editor
                    </CButton>
                  </CTooltip>
                </div> */}
              </CCol>
              <CCol md={6}>
                <CFormLabel>Change Profile Picture</CFormLabel>
                <CFormInput
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setSelectedFile(file);
                  }}
                />
              </CCol>
            </CRow>

            <CRow className="mb-4">
              <CCol md={6}>
                <CFormLabel>Name</CFormLabel>
                <CFormInput
                  value={formValues.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
                {errors.name && (
                  <div className="text-danger">{errors.name}</div>
                )}
              </CCol>

              <CCol md={6}>
                <CFormLabel>Company Name</CFormLabel>
                <CFormInput
                  value={formValues.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                />
                {errors.company_name && (
                  <div className="text-danger">{errors.company_name}</div>
                )}
              </CCol>
            </CRow>

            <CRow className="mb-4">
              <CCol md={6}>
                <CFormLabel>Email</CFormLabel>
                <CFormInput
                  value={formValues.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
                {errors.email && (
                  <div className="text-danger">{errors.email}</div>
                )}
              </CCol>

              <CCol md={6}>
                <CFormLabel>Phone</CFormLabel>

                {/*
                  HOW IT WORKS:
                  • countryCode dropdown sets formValues.countryCode (e.g. "+61")
                  • text input sets formValues.phone (local digits only, e.g. "412345678")
                  • On submit they are merged: phone = `${countryCode}${phone}`
                  • On load, splitPhone() splits the stored "+61412345678" back apart
                */}
                <div
                  className="d-flex gap-0"
                  style={{
                    border: "1px solid #b1b7c1",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  {/* Country Code Selector */}
                  <select
                    value={formValues.countryCode}
                    onChange={(e) =>
                      handleChange("countryCode", e.target.value)
                    }
                    style={{
                      border: "none",
                      borderRight: "1px solid #b1b7c1",
                      padding: "6px 4px",
                      fontSize: "0.875rem",
                      minWidth: "90px",
                      backgroundColor: "#f8f9fa",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  >
                    {COUNTRY_CODES.map((country) => (
                      <option key={country.value} value={country.code}>
                        {country.value} {country.code}
                      </option>
                    ))}
                  </select>

                  {/* Local Number Input */}
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formValues.phone}
                    onChange={(e) =>
                      handleChange("phone", e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="Phone number"
                    style={{
                      flex: 1,
                      border: "none",
                      padding: "6px 10px",
                      fontSize: "0.875rem",
                      outline: "none",
                    }}
                  />
                </div>

                {/* Live preview of the full number */}
                {formValues.phone && (
                  <div className="text-muted small mt-1">
                    Full number: {formValues.countryCode}
                    {formValues.phone}
                  </div>
                )}
                {errors.phone && (
                  <div className="text-danger small mt-1">{errors.phone}</div>
                )}
              </CCol>
            </CRow>

            {/* <CRow className="mb-4"> */}
            {/* Communication Email */}
            {/* <CCol md={6}>
                <CFormLabel>Communication Email</CFormLabel>
                <CFormInput
                  value={formValues.communication_email}
                  onChange={(e) =>
                    handleChange("communication_email", e.target.value)
                  }
                />

                <CFormSwitch
                  className="mt-2"
                  label="Use for general communication"
                  checked={Boolean(formValues.is_email_visible)}
                  onChange={(e) =>
                    handleChange("is_email_visible", e.target.checked ? 1 : 0)
                  }
                />
              </CCol> */}

            {/* Communication Phone */}
            {/* <CCol md={6}>
                <CFormLabel>Communication Phone</CFormLabel>
                <CFormInput
                  value={formValues.communication_phone}
                  onChange={(e) =>
                    handleChange("communication_phone", e.target.value)
                  }
                />

                <CFormSwitch
                  className="mt-2"
                  label="Use for general communication"
                  checked={Boolean(formValues.is_phone_visible)}
                  onChange={(e) =>
                    handleChange("is_phone_visible", e.target.checked ? 1 : 0)
                  }
                />
              </CCol> */}
            {/* </CRow> */}

            {/* {console.log('tradeOptions:', tradeOptions)}     */}
            <CRow className="mb-4">
              <CCol md={6}>
                <CFormLabel>Role</CFormLabel>
                <CFormInput
                  value={capitalizeFirst(formValues.role)}
                  readOnly
                  plaintext
                  className="bg-light"
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel>Trade Preferences</CFormLabel>
                <div className="d-flex flex-wrap gap-2">
                  {tradeOptions.map((pref) => {
                    const isSelected = formValues.trade_preferences?.some(
                      (id) => Number(id) === Number(pref.id),
                    );
                    // console.log('isSelected:',isSelected, 'for pref:', pref);
                    // console.log("formValues.trade_preferences:", formValues.trade_preferences);
                    return (
                      <CButton
                        key={pref.id}
                        color={isSelected ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => {
                          let updated = isSelected
                            ? formValues.trade_preferences.filter(
                                (id) => Number(id) !== Number(pref.id),
                              )
                            : [
                                ...formValues.trade_preferences,
                                Number(pref.id),
                              ];
                          handleChange("trade_preferences", updated);
                        }}
                      >
                        {pref.name}
                      </CButton>
                    );
                  })}
                  {errors.trade_preferences && (
                    <div className="text-danger">
                      {errors.trade_preferences}
                    </div>
                  )}
                </div>
              </CCol>
            </CRow>

            <div className="text-end">
              <CButton color="primary" className="me-2" onClick={handleSubmit}>
                Save
              </CButton>
              <CButton color="secondary" onClick={() => window.history.back()}>
                Cancel
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CContainer>

      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />

      {/* AI Modal */}
      <CModal visible={showAiModal} onClose={() => setShowAiModal(false)}>
        <CModalHeader>
          <CModalTitle>AI Preview</CModalTitle>
        </CModalHeader>

        <CModalBody className="text-center">
          {/* Dual Preview Row */}
          <div className="d-flex gap-3 justify-content-center flex-wrap mb-3">
            {/* Original Preview */}
            <div className="d-flex flex-column align-items-center">
              <span className="small fw-bold text-muted mb-1">Original</span>
              <img
                src={
                  (selectedFile && URL.createObjectURL(selectedFile)) ||
                  previewImage ||
                  dummyAvatar
                }
                alt="Original"
                style={{
                  width: 180,
                  height: 180,
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  objectFit: "cover",
                }}
              />
            </div>

            {/* AI Preview */}
            <div className="d-flex flex-column align-items-center">
              <span className="small fw-bold text-primary mb-1">
                AI Preview
              </span>
              <div style={{ position: "relative" }}>
                <img
                  src={tempAiPreview || dummyAvatar}
                  alt="AI Preview"
                  style={{
                    width: 180,
                    height: 180,
                    borderRadius: 8,
                    border: "2px solid #007bff",
                    filter: editImageLoading ? "blur(5px)" : "none",
                    transition: "filter 0.3s ease",
                    backgroundColor: "#f8f9fa",
                    objectFit: "cover",
                  }}
                />

                {editImageLoading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: 8,
                      background: "rgba(0,0,0,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress color="inherit" size={30} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="small text-muted mb-3">
            {editImageLoading
              ? "Processing your image..."
              : "Choose an action below to see the AI magic"}
          </div>

          {/* AI Action Buttons */}
          <div className="d-flex justify-content-center gap-2">
            <CButton
              color="primary"
              disabled={editImageLoading}
              onClick={() => handleEdit("enhance")}
            >
              <FaImage className="me-1" />
              Enhance
            </CButton>
            <CButton
              color="primary"
              disabled={editImageLoading}
              onClick={() => handleEdit("remove_bg")}
            >
              <FaEraser className="me-1" />
              Remove BG
            </CButton>
          </div>

          <style>
            {`
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      `}
          </style>
        </CModalBody>

        <CModalFooter className="d-flex justify-content-between">
          <CButton
            color="secondary"
            onClick={() => {
              setTempAiPreview(null);
              setShowAiModal(false);
            }}
          >
            Cancel
          </CButton>
          <CButton
            color="primary"
            disabled={!tempAiPreview}
            onClick={applyAiPreview}
          >
            Apply
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default SellerEditProfile;
